import { ThemePart } from './ThemePart';
import * as events from './events';
import { ThemeModule } from './ThemeModule';
import { ThemeLayout } from './ThemeLayout';
export class ThemeBase extends ThemePart {
    get $partType$() { return 'troot'; }
    constructor(el) {
        super(document.documentElement);
        /**
         * @internal Used to observe addition of new modules and connected components to DOM
         */
        this.$addedNodesObserver$ = null;
        this.$layout$ = new ThemeLayout.UnknownLayout(document.documentElement);
        this.$config$ = null;
    }
    get name() {
        return 'Theme';
    }
    get id() {
        return 'troot';
    }
    destroy() {
    }
    static get instance() {
        return this.$instance$;
    }
    get layout() {
        return this.$layout$;
    }
    get config() {
        return this.$config$;
    }
    static triggerAfterInitRec(module) {
        for (let mod of module.submodules.list) {
            this.triggerAfterInitRec(mod);
        }
        module.afterInit();
    }
    static init(config = {}) {
        if (!this.$instance$) {
            const cls = this;
            this.$instance$ = new cls(document.documentElement);
            this.$instance$.$config$ = config;
            const [layout, modules] = this.$instance$.initParts(config === null || config === void 0 ? void 0 : config.layouts, config === null || config === void 0 ? void 0 : config.modules);
            this.$instance$.$layout$ = layout;
            for (let mod of modules) {
                this.$instance$.submodules.add(mod);
            }
            this.$instance$.$layout$.init();
            this.$instance$.init();
            for (let mod of modules) {
                this.triggerAfterInitRec(mod);
            }
            this.$instance$.$layout$.afterInit();
            this.$instance$.afterInit();
            window.$theme = this.$instance$;
        }
        return this.$instance$;
    }
    initParts(layoutsDefs = {}, modulesDefs = {}) {
        const enabledModules = Object.keys(modulesDefs);
        const enabledLayouts = Object.keys(layoutsDefs);
        const layout = (() => {
            const maybeLayoutEl = document.querySelector('[data-tlayout]');
            const layoutId = maybeLayoutEl === null || maybeLayoutEl === void 0 ? void 0 : maybeLayoutEl.dataset.tlayout;
            if (maybeLayoutEl && enabledLayouts.includes(layoutId)) {
                return new layoutsDefs[layoutId](maybeLayoutEl);
            }
            else if (maybeLayoutEl) {
                return new ThemeLayout.UnknownLayout(maybeLayoutEl);
            }
            else {
                return new ThemeLayout.UnknownLayout(document.body);
            }
        })();
        if (!this.$addedNodesObserver$) {
            this.$addedNodesObserver$ = new MutationObserver(entries => entries.forEach(entry => {
                if (entry.type === 'childList') {
                    if (entry.addedNodes.length) {
                        [].slice.apply(entry.addedNodes)
                            .filter((el) => el instanceof HTMLElement)
                            .reverse()
                            .forEach(node => {
                            var _a;
                            const topModules = mapModules(node);
                            // Sort modules by their relative position in DOM
                            // so that we can add them to their containers in correct order
                            topModules.sort((a, b) => {
                                const aEl = a.root;
                                const bEl = b.root;
                                if (aEl === bEl) {
                                    return 0;
                                }
                                return aEl.compareDocumentPosition(bEl) & Node.DOCUMENT_POSITION_PRECEDING ? 1 : -1;
                            });
                            if (topModules.length) {
                                for (let mod of topModules) {
                                    const containerEl = (_a = mod.root.parentElement) === null || _a === void 0 ? void 0 : _a.closest('[data-tmod], [data-tlayout]');
                                    const container = containerEl && this.submodules.all.find(p => p.root === containerEl);
                                    if (container) {
                                        const maybeDuplicate = container.submodules.list.find(p => p.id === mod.id);
                                        if (maybeDuplicate) {
                                            maybeDuplicate.destroy();
                                        }
                                        container.submodules.add(mod);
                                    }
                                    else {
                                        this.submodules.add(mod);
                                    }
                                    ThemeBase.triggerAfterInitRec(mod);
                                }
                            }
                        });
                    }
                }
            }));
            this.$addedNodesObserver$.observe(document.body, {
                subtree: true,
                childList: true,
            });
        }
        const mapModules = (root) => {
            const topModules = [];
            const allEls = [].slice.apply(root.querySelectorAll(`[data-tmod]`))
                .filter((el) => el.dataset.tmod && !el.$initialized$);
            if (root.matches(`[data-tmod]`)) {
                allEls.push(root);
            }
            if (!allEls.length) {
                return [];
            }
            const actionTriggers = [].slice.apply(root.querySelectorAll('[data-theme-action-trigger]'));
            actionTriggers.forEach(initActionTriggers);
            const moduleList = [];
            for (let el of allEls) {
                const moduleId = el.dataset.tmod;
                if (moduleId && !el.$initialized$) {
                    try {
                        const module = enabledModules.includes(moduleId)
                            ? new modulesDefs[moduleId](el) : new ThemeModule.UnknownModule(el);
                        module.init();
                        module.root.$initialized$ = true;
                        moduleList.push(module);
                    }
                    catch (error) {
                        console.warn(error);
                    }
                }
            }
            // Create submodule tree without recursion by tracking parent modules
            let parentModules = [];
            for (let module of moduleList) {
                if (parentModules.length) {
                    let slice = 0;
                    for (let i = 0; i < parentModules.length; i++) {
                        const prev = parentModules[i];
                        if (prev.root.contains(module.root)) {
                            prev.submodules.add(module);
                            break;
                        }
                        else {
                            slice++;
                        }
                    }
                    parentModules = parentModules.slice(slice);
                }
                if (!parentModules.length) {
                    topModules.push(module);
                }
                parentModules.unshift(module);
            }
            return topModules;
        };
        const initActionTriggers = (node) => {
            if (node.dataset.themeActionTrigger) {
                const args = node.dataset.themeActionTriggerArgs ? JSON.parse(node.dataset.themeActionTriggerArgs) : {};
                node.addEventListener('click', event => {
                    if (!node.dataset.themeActionTriggerDefault) {
                        event.preventDefault();
                    }
                    events.dispatch(node, node.dataset.themeActionTrigger, args);
                });
            }
        };
        const modules = mapModules(document.body);
        for (let mod of modules) {
            if (layout.root.contains(mod.root)) {
                layout.submodules.add(mod);
            }
        }
        return [layout, modules];
    }
}
